/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { CampaignStepAPI } from "../../../api/CampaignStep";
import { ContactCampaignStepsAtom } from "../../../atoms/CampaignAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { CreateCampaignStepIRoute } from "../../../generated/Api";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { INode, INodeTypes } from "../../../types/CampaignTypes";
import { IContact } from "../../../types/Contact";
import { ITag } from "../../../types/TagTypes";
import IconBerserkerMail from "../../Icon";
import SearchInput from "../../SearchInput";
import { TableFooter } from "../../StyledTable";
import Tag from "../../Tag";
import { DateFilter, EmptyContainer, SkeletonCard } from "./Emails";
import FilterCampaigns, { FilterCampaign } from "./FilterCampaigns";

const CampaignStepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  cursor: default;
`;

const TagNameWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  grid-gap: 8px;
`;

const NoteRow = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: 240px 0.5fr 1fr;
  grid-column-gap: 20px;
  width: 100%;
  background: #f8f8f8 0% 0% no-repeat padding-box;
  border-radius: 2px;
  margin-bottom: 10px;

  :last-child {
    margin-bottom: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-end;
`;

const Between = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const Circle = styled.div`
  display: grid;
  align-items: center;
  border: 1px solid;
  border-color: #868482;
  background-color: #fff;
  height: 40px;
  width: 40px;
  border-radius: 100%;

  flex-shrink: 0;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;

  .title {
    margin-bottom: 5px;
  }

  .info {
    display: flex;
    align-items: center;
    width: 80px;
  }

  .text {
    font-weight: 400;
  }
`;

const Text = styled.div`
  color: #333333;
  font-size: 14px;

  white-space: nowrap;
`;

const SubTitle = styled.div`
  color: #333333;
  font-size: 18px;
  font-weight: 600;
`;
interface Props {
  contact: IContact;
}

export const NODE_DESCRIPTIONS_MAP: { [key: string]: string } = {
  [INodeTypes.ManageTag]: "Tags applied or removed",
  [INodeTypes.StopTag]: "Campaign stopped",
  [INodeTypes.Email]: "Email sent",
  [INodeTypes.WebHook]: "Webhook triggered",
  [INodeTypes.PushNotification]: "Turn on notifications",
  [INodeTypes.Sms]: "SMS notification sent",
};

const ICON_MAP: { [key: string]: string } = {
  [INodeTypes.ManageTag]: "start-tag",
  applyTag: "start-tag",
  removeTag: "start-tag",
  [INodeTypes.StopTag]: "closed-circle",
  [INodeTypes.Email]: "message",
  [INodeTypes.WebHook]: "webhook",
  [INodeTypes.PushNotification]: "bell",
  [INodeTypes.Sms]: "sms",
};

function getActionContent(node: INode, tags: ITag[]): JSX.Element | string {
  switch (node.type) {
    case INodeTypes.ManageTag: {
      return (
        <Row>
          {!!node.manageTag.addedTags?.length && (
            <Column
              css={css`
                flex: 1;
                margin-left: 0;
              `}
            >
              <Text className="title">Added tag(s):</Text>
              <TagNameWrapper>
                {node.manageTag.addedTags.map((campaignTags) => {
                  let tag = tags.find((tag) => tag._id === campaignTags._id);

                  if (tag)
                    return (
                      <Tag
                        customStyle={{ margin: "0", marginRight: "10px" }}
                        key={`${tag?._id}_${tag?.name}`}
                        tag={tag}
                      />
                    );
                })}
              </TagNameWrapper>
            </Column>
          )}
          {!!node.manageTag.removedTags?.length && (
            <Column
              css={css`
                flex: 1;
              `}
            >
              <Text className="title">Removed tag(s):</Text>
              <TagNameWrapper>
                {node.manageTag?.removedTags.map((campaignTags) => {
                  let tag = tags.find((tag) => tag._id === campaignTags._id);

                  if (tag)
                    return (
                      <Tag
                        customStyle={{ margin: "0", marginRight: "10px" }}
                        key={`${tag?._id}_${tag?.name}`}
                        tag={tag}
                      />
                    );
                })}
              </TagNameWrapper>
            </Column>
          )}
        </Row>
      );
    }
    case INodeTypes.StopTag: {
      return (
        <Row>
          {!!node.stopTag.tags?.length && (
            <Column
              css={css`
                flex: 1;
              `}
            >
              <Text className="title">Stop tag(s):</Text>
              <TagNameWrapper>
                {node.stopTag.tags.map((campaignTags) => {
                  let tag = tags.find((tag) => tag._id === campaignTags._id);

                  if (tag)
                    return (
                      <Tag
                        customStyle={{ margin: "0", marginRight: "10px" }}
                        key={`${tag?._id}_${tag?.name}`}
                        tag={tag}
                      />
                    );
                })}
              </TagNameWrapper>
            </Column>
          )}
        </Row>
      );
    }
    case INodeTypes.Email: {
      return (
        <>
          <Text className="title info">Email Subject</Text>

          <SubTitle>{node.email?.subject}</SubTitle>
        </>
      );
    }
    case INodeTypes.WebHook: {
      return (
        <>
          <Text className="title info">Webhook URL</Text>

          <SubTitle>{node.name}</SubTitle>
        </>
      );
    }
    case INodeTypes.PushNotification: {
      return node.name || <div />;
    }
    case INodeTypes.Sms: {
      return node.name || <div />;
    }
    default: {
      return node.name || <div />;
    }
  }
}

export default function CampaignSteps(props: Props) {
  const [campaignSteps, setCampaignSteps] = useRecoilState(
    ContactCampaignStepsAtom
  );
  const tags = useRecoilValue(TagsAtom);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [filter, setFilter] = useState<FilterCampaign>({
    type: [],
    date: DateFilter.Recent,
  });

  const [filteredCampaigns, setFilteredCampaigns] = useState<
    CreateCampaignStepIRoute[]
  >([]);

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.CAMPAIGNS
  );

  useEffect(() => {
    (async () => {
      try {
        const newCampaignSteps =
          await CampaignStepAPI.getCampaignStepsFromContact(props.contact._id);
        if (!newCampaignSteps) return;
        setCampaignSteps(newCampaignSteps);

        if (newCampaignSteps.length < 1) {
          setIsEmpty(true);
        }
      } catch (err) {
        console.log("err", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setFilteredCampaigns(getFilteredCampaigns());
  }, [campaignSteps, filter, campaignFilter]);

  function getFilteredCampaigns() {
    if (!campaignSteps) return [];

    let campaignStepsWithFilter: CreateCampaignStepIRoute[] = [
      ...campaignSteps,
    ];

    if (filter.type.length > 0) {
      campaignStepsWithFilter = campaignStepsWithFilter.filter((not) => {
        if (filter.type.find((t) => t === not.node.type)) return true;
        return false;
      });
    }

    return campaignStepsWithFilter
      .filter((not) => {
        const campaignName = not.campaign.name.trim().toLowerCase();
        const sanitizedCampaignFilter = campaignFilter.trim().toLowerCase();

        if (not.node.manageTag) {
          return (
            campaignName.includes(sanitizedCampaignFilter) ||
            [
              ...(not.node.manageTag.addedTags || []),
              ...(not.node.manageTag.removedTags || []),
            ].filter((t) =>
              t.name
                .trim()
                .toLowerCase()
                .includes(campaignFilter.toLowerCase().trim())
            ).length
          );
        }

        let nameToFilter = "";

        if (not.node.email) {
          nameToFilter = not.node.email.subject;
        } else if (not.node.pushNotification) {
          nameToFilter =
            not.node.pushNotification.content + not.node.pushNotification.title;
        } else if (not.node.sms) {
          nameToFilter = not.node.sms.message;
        } else if (not.node.webHook?.url) {
          nameToFilter = not.node.name;
        }

        return (
          campaignName.includes(sanitizedCampaignFilter) ||
          nameToFilter.trim().toLowerCase().includes(sanitizedCampaignFilter)
        );
      })
      .sort((a, b) => {
        if (filter.date === DateFilter.Recent)
          return dayjs(b.enteredAt).diff(a.enteredAt);
        else return dayjs(a.enteredAt).diff(b.enteredAt);
      });
  }

  function handlePage(vle: number) {
    setPage(vle);
  }

  if (loading)
    return (
      <CampaignStepsContainer>
        <Between>
          <SubTitle>Campaigns</SubTitle>

          <Buttons>
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for campaigns"
              value={campaignFilter}
              handleChange={(vle) => setCampaignFilter(vle)}
            />
            <FilterCampaigns
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
          </Buttons>
        </Between>

        {Array(5)
          .fill(0)
          .map((e) => (
            <SkeletonCard
              css={css`
                width: 100%;
                height: 80px;
                margin-bottom: 10px;
              `}
            />
          ))}
      </CampaignStepsContainer>
    );

  if (isEmpty) {
    return getEmptyContainer();
  }

  function getEmptyContainer() {
    let textToShow: string;

    if (campaignFilter) {
      textToShow = "No campaigns matching your criteria";
    } else {
      if (filter.type.length) {
        textToShow = "No campaigns matching your criteria";
      } else {
        textToShow = "No campaigns to show";
      }
    }

    return (
      <EmptyContainer>
        <IconBerserkerMail name="campaign-empty" />
        <SubTitle
          css={css`
            margin-top: 15.8px;
            font-size: 16px;
          `}
        >
          {textToShow}
        </SubTitle>
      </EmptyContainer>
    );
  }

  return (
    <CampaignStepsContainer>
      {(filteredCampaigns.length > 0 || campaignFilter || filter) && (
        <Between>
          <SubTitle>Campaigns</SubTitle>

          <Buttons>
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for campaigns"
              value={campaignFilter}
              handleChange={(vle) => setCampaignFilter(vle)}
            />
            <FilterCampaigns
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
          </Buttons>
        </Between>
      )}

      {filteredCampaigns.length > 0 ? (
        <>
          {filteredCampaigns
            ?.filter(
              (n) =>
                new Date(n.enteredAt).getTime() !== new Date(0).getTime() ||
                n.current
            )
            .slice(
              page * (cachedRowsPerPage || rowsPerPage),
              page * (cachedRowsPerPage || rowsPerPage) +
                (cachedRowsPerPage || rowsPerPage)
            )
            .map((n) => {
              return (
                <NoteRow>
                  <Row>
                    <Circle>
                      <IconBerserkerMail
                        name={ICON_MAP[n.node.type]}
                        color="#333"
                      />
                    </Circle>
                    <Column>
                      <Text className="title">
                        {new Date(n.enteredAt).getTime() ===
                        new Date(0).getTime()
                          ? "_"
                          : dayjs(n.enteredAt).format(
                              "MMM D, YYYY [@] h:mm a (z)"
                            )}
                      </Text>
                      <SubTitle>{NODE_DESCRIPTIONS_MAP[n.node.type]}</SubTitle>
                    </Column>
                  </Row>
                  <Row>
                    <Column
                      css={css`
                        flex: 1;
                      `}
                    >
                      <Text className="title">Campaign</Text>
                      <SubTitle>{n.campaign.name}</SubTitle>
                    </Column>
                  </Row>
                  <Row>
                    <Column
                      css={css`
                        flex: 1;
                      `}
                    >
                      {getActionContent(n.node as any, tags)}
                    </Column>
                  </Row>
                </NoteRow>
              );
            })}
          <TableFooter
            isSmaller
            page={page}
            length={
              filteredCampaigns?.filter(
                (n) =>
                  new Date(n.enteredAt).getTime() !== new Date(0).getTime() ||
                  n.current
              ).length || 0
            }
            rowsPerPage={cachedRowsPerPage || rowsPerPage}
            setRowsPerPage={(vle: number) => {
              setRowsPerPage(vle);
              updateCachedRowsPerPage(vle);
            }}
            handlePage={handlePage}
          />
        </>
      ) : (
        getEmptyContainer()
      )}
    </CampaignStepsContainer>
  );
}
